import startOrEndDateForService from '../../../../services/startOrEndDateForService';

const YEAR_MONTH = /^(\d{4})-(\d{2})$/;
const YEAR_MONTH_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Letzter Kalendertag eines Monats — zeitzonenunabhängig (reine Arithmetik).
 */
function lastDayOfMonth(year, month /* 1-12 */) {
  if (month === 2) {
    const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
    return leap ? 29 : 28;
  }
  if (month === 4 || month === 6 || month === 9 || month === 11) {
    return 30;
  }
  return 31;
}

/**
 * Wandelt Kalendertage (YYYY-MM-DD) und Date-Objekte in ein lokales Date um.
 * YYYY-MM wird hier nicht mehr abgebildet — siehe resolveServiceDate.
 */
function toLocalDateInput(date) {
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'string') {
    const ymd = date.match(YEAR_MONTH_DAY);
    if (ymd) {
      return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    }
  }
  return date;
}

/**
 * Serialisiert neu gewählte Service-Daten für den Server.
 * - ''/null: 1:1 (end_date-Semantik bleibt erhalten)
 * - YYYY-MM-DD: 1:1 durchreichen
 * - YYYY-MM (neu gewählt): direkt als String monatsabbilden (kein lokales Date)
 *   Monthly/Yearly + endDate → letzter Kalendertag, sonst → Tag 01
 * Unveränderte Felder laufen nicht hier durch — die kommen 1:1 aus rawServiceDatesById.
 */
function resolveServiceDate({ date, serviceType, startOrEndDate }) {
  if (date === '' || date == null) {
    return date;
  }
  // Vollständige Kalendertage unverändert durchreichen.
  if (typeof date === 'string' && YEAR_MONTH_DAY.test(date)) {
    return date;
  }
  // Anzeigeformat YYYY-MM — neu gewählt — direkt als String abbilden (kein lokales Date).
  if (typeof date === 'string' && YEAR_MONTH.test(date)) {
    const ym = date.match(YEAR_MONTH);
    const year = Number(ym[1]);
    const month = Number(ym[2]);
    if ((serviceType === 'MonthlyService' || serviceType === 'YearlyService') && startOrEndDate === 'endDate') {
      const day = String(lastDayOfMonth(year, month)).padStart(2, '0');
      return `${date}-${day}`;
    }
    return `${date}-01`;
  }
  return startOrEndDateForService({
    serviceType,
    date: toLocalDateInput(date),
    startOrEndDate,
  });
}

function commitChanges({
  changed,
  deleted,
  rows,
  refetch,
  updateAgentService,
  deleteAgentService,
  rawServiceDatesById,
}) {
  let changedRows;

  if (changed) {
    const currentRows = [...rows];
    changedRows = rows.map((row) => (changed[row.id] ? { ...row, ...changed[row.id] } : row));

    let input = {};
    for (let i = 0; i < currentRows.length; i += 1) {
      if (currentRows[i] !== changedRows[i]) {
        const rowChange = changed[changedRows[i].id] || {};
        const startChanged = Object.prototype.hasOwnProperty.call(rowChange, 'start_date');
        const endChanged = Object.prototype.hasOwnProperty.call(rowChange, 'end_date');
        const rawDates =
          rawServiceDatesById && Object.prototype.hasOwnProperty.call(rawServiceDatesById, changedRows[i].id)
            ? rawServiceDatesById[changedRows[i].id]
            : undefined;

        // Unveränderte Datumswerte: gespeicherten Rohwert 1:1 (kein Re-Parse/Monatsabbildung).
        // Nur tatsächlich geänderte Felder (im changed-Objekt) neu auflösen.
        // Drei-Wege: changed → resolve; sonst raw (inkl. null); sonst row-Wert.
        let startDate;
        if (startChanged) {
          startDate = resolveServiceDate({
            date: changedRows[i].start_date,
            serviceType: changedRows[i].service_type,
            startOrEndDate: 'startDate',
          });
        } else if (rawDates) {
          startDate = rawDates.start_date;
        } else {
          startDate = changedRows[i].start_date;
        }
        // end_date Drei-Zustands-Semantik (nur Key-Präsenz in changed, nie Truthiness):
        // (1) changed mit Datumswert → serialisiert übertragen
        // (2) changed mit null/Leerwert (✕) → end_date: null
        // (3) changed ohne Key end_date → Schlüssel fehlt im Input (kein Rohwert-Echo)
        let endDate;
        if (endChanged) {
          endDate = resolveServiceDate({
            date: changedRows[i].end_date,
            serviceType: changedRows[i].service_type,
            startOrEndDate: 'endDate',
          });
        }

        if (startDate || endChanged) {
          input = {
            agent_id: changedRows[i].agent_service_agent_id,
            service_id: changedRows[i].id,
            price: parseFloat(changedRows[i].agent_service_price),
            description: changedRows[i].agent_service_description,
            start_date: startDate,
          };
          if (endChanged) {
            if (endDate == null || endDate === '') {
              input.end_date = null;
            } else {
              input.end_date = endDate;
            }
          }
          if (changedRows[i].customer_id !== null && changedRows[i].customer_id.trim() !== '') {
            input.customer_id = changedRows[i].customer_id;
          }
        } else {
          input = {
            agent_id: changedRows[i].agent_service_agent_id,
            service_id: changedRows[i].id,
            price: parseFloat(changedRows[i].agent_service_price),
            description: changedRows[i].agent_service_description,
          };
          if (changedRows[i].customer_id !== null && changedRows[i].customer_id.trim() !== '') {
            input.customer_id = changedRows[i].customer_id;
          }
        }
      }
    }

    if (changedRows) {
      updateAgentService({ variables: { variables: input } }).then((resultat) => {
        if (resultat.data) refetch();
      });
    }
  }
  if (deleted) {
    const deletedSet = new Set(deleted);
    changedRows = rows.filter((row) => !deletedSet.has(row.id));
    const setIter = deletedSet.values();
    const setValue = setIter.next().value;
    const id = parseInt(setValue, 10);
    if (id) {
      deleteAgentService({ variables: { id } }).then((result) => {
        if (result.data) refetch();
      });
    }
  }
}

export default commitChanges;
